import React from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

/**
 * ErrorBoundary — catches render errors from a subtree and shows a compact
 * fallback card instead of unmounting the whole page. Used to isolate
 * individual widgets (charts, tabs, galleries) so one failing component
 * never blanks the parent screen.
 *
 * Usage:
 *   <ErrorBoundary label="Progress chart">
 *     <ProgressOverTime .../>
 *   </ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep console noise minimal — one grouped log per crash is enough.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary] ${this.props.label || "component"} crashed`, error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(this.state.error, this.reset);
    }
    return (
      <div
        role="alert"
        data-testid={this.props.testId || "error-boundary"}
        className="bg-[#1a0e0e] border border-[#FF3B30]/40 rounded-lg p-4 text-sm text-[#FFB4AF]"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="text-[#FF6B61] mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-bold text-[#FFB4AF]">
              {this.props.label || "This section"} couldn’t load.
            </div>
            <div className="text-xs text-[#FF9F94] mt-0.5 break-words">
              {String(this.state.error?.message || this.state.error)}
            </div>
            <button
              type="button"
              onClick={this.reset}
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-white bg-[#FF3B30]/80 hover:bg-[#FF3B30] px-3 py-1.5 rounded"
            >
              <RotateCcw size={11} /> Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
